import {
  IconWarning,
} from '../components/icons';
import {
  MetricGrid,
  MetricTile,
  Select,
  StatusPill,
} from '../components/ui';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { reviewAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const ReviewDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState({
    status: '',
    content_type: '',
    priority: '',
  });

  // Fetch reviews and stats
  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const userId = user?.id || null;
      // Fetch both reviews and stats in parallel
      const [reviewsData, statsData] = await Promise.all([
        reviewAPI.list(filter, userId),
        reviewAPI.getStats(userId),
      ]);

      setReviews(reviewsData.reviews || []);
      setStats(statsData);
    } catch (err) {
      console.error('Failed to fetch reviews:', err);
      setError(err.userMessage || 'Failed to load reviews. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load data on mount and when filter changes
  useEffect(() => {
    fetchData();
  }, [filter.status, filter.content_type, filter.priority]);

  // Handle filter change
  const handleFilterChange = (field, value) => {
    setFilter(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Semantic tone for the status pill. Meaning is carried by the label and a
  // dot, so it survives being read in greyscale.
  const statusTone = (status) =>
    ({ pending: 'warn', in_review: 'accent', approved: 'good', rejected: 'bad', edited: 'neutral' }[status] ||
      'neutral');

  const priorityTone = (priority) =>
    ({ urgent: 'bad', high: 'warn', normal: 'neutral', low: 'neutral' }[priority] || 'neutral');

  // Sentence case: "In review", not "IN_REVIEW".
  const humanise = (value = '') =>
    value.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

  // Get content type label
  const getContentTypeLabel = (type) => {
    const labels = {
      sms: 'SMS',
      voice_script: 'Voice',
      ivr_flow: 'AI Voice',
      whatsapp: 'WhatsApp',
      multi_channel: 'Multi-Channel',
    };
    return labels[type] || type;
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Review Dashboard
        </h1>
        <p className="text-gray-600">
          Review and approve AI-generated content before delivery
        </p>
      </div>

      {/* Statistics */}
      {stats && (
        <MetricGrid className="mb-6 grid-cols-2 lg:grid-cols-4">
          <MetricTile label="Total reviews" value={stats.total_reviews} />
          <MetricTile
            label="Pending"
            value={stats.pending_reviews}
            dot
            statusTone={stats.pending_reviews > 0 ? 'warn' : 'neutral'}
            hint={stats.pending_reviews === 1 ? 'awaiting sign-off' : 'awaiting sign-off'}
          />
          <MetricTile label="Approved" value={stats.approved_reviews} dot statusTone="good" />
          <MetricTile
            label="Unverified claims"
            value={stats.unverified_claims_total}
            dot
            statusTone={stats.unverified_claims_total > 0 ? 'bad' : 'neutral'}
            hint={stats.unverified_claims_total > 0 ? 'needs a clinician' : 'all verified'}
          />
        </MetricGrid>
      )}

      {/* Filters */}
      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Select
          label="Status"
          value={filter.status}
          onChange={(e) => handleFilterChange('status', e.target.value)}
        >
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="in_review">In review</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="edited">Edited</option>
        </Select>

        <Select
          label="Content type"
          value={filter.content_type}
          onChange={(e) => handleFilterChange('content_type', e.target.value)}
        >
          <option value="">All types</option>
          <option value="sms">SMS</option>
          <option value="voice_script">Voice</option>
          <option value="ivr_flow">AI Voice</option>
          <option value="whatsapp">WhatsApp</option>
        </Select>

        <Select
          label="Priority"
          value={filter.priority}
          onChange={(e) => handleFilterChange('priority', e.target.value)}
        >
          <option value="">All priorities</option>
          <option value="urgent">Urgent</option>
          <option value="high">High</option>
          <option value="normal">Normal</option>
          <option value="low">Low</option>
        </Select>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Reviews List */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : reviews.length === 0 ? (
        <div className="bg-white p-12 rounded-lg shadow text-center">
          <p className="text-gray-500 text-lg">No reviews found</p>
          <p className="text-gray-400 mt-2">
            {filter.status || filter.content_type || filter.priority
              ? 'Try adjusting your filters'
              : 'Create a campaign to generate content for review'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <div
              key={review.review_id}
              className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => navigate(`/review/${review.review_id}`)}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    {review.campaign_name || review.disease_name}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {review.disease_name} • {getContentTypeLabel(review.content_type)} • {review.language}
                  </p>
                </div>
                <div className="flex flex-shrink-0 gap-2">
                  <StatusPill tone={statusTone(review.status)}>
                    {humanise(review.status)}
                  </StatusPill>
                  <StatusPill tone={priorityTone(review.metadata.priority)} dot={false}>
                    {humanise(review.metadata.priority)}
                  </StatusPill>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <p className="text-xs text-gray-500">Recipients</p>
                  <p className="text-sm font-medium text-gray-900">
                    {review.recipients_count}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Created</p>
                  <p className="text-sm font-medium text-gray-900">
                    {formatDate(review.created_at)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Campaign ID</p>
                  <p className="text-sm font-medium text-gray-900 font-mono">
                    {review.campaign_id || review.review_id.slice(0, 12)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Unverified Claims</p>
                  <p className={`text-sm font-medium ${review.unverified_claims_count > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                    {review.unverified_claims_count}
                  </p>
                </div>
              </div>

              {review.unverified_claims_count > 0 && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm">
                  <IconWarning className="h-4 w-4 shrink-0" /> Contains {review.unverified_claims_count} unverified medical claim(s)
                </div>
              )}

              {review.metadata.flagged_issues && review.metadata.flagged_issues.length > 0 && (
                <div className="mt-2 flex gap-2 flex-wrap">
                  {review.metadata.flagged_issues.map((issue, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-md"
                    >
                      {issue.replace('_', ' ')}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewDashboard;
